import { FC, useMemo } from 'react';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { useTranslation } from '@frontend/i18n';
import { PersonSelectorComponents, PersonSelectorHooks } from '@frontend/person-selector';
import { ContentLoader, emptyStateGraphics, Text } from '@frontend/design-system';
import { useSteps } from '../../../hooks/useSteps';
import { useSendFormsStore } from '../../../providers/store';
import {
  listContainerStyle,
  filterSpacingStyle,
  imageStyle,
  imageContainerStyle,
  centerTextStyle,
  containerStyle,
} from './recipient-selection.styles';

const EmptyStateGraphicGeneric = emptyStateGraphics.generic;

interface PersonsProps {
  persons: ReturnType<typeof PersonSelectorHooks.useSelectRecipientList>['persons'];
  isLoading: ReturnType<typeof PersonSelectorHooks.useSelectRecipientList>['isLoading'];
}

const Persons: FC<PersonsProps> = ({ isLoading, persons }) => {
  const { t } = useTranslation('digital-forms-sending-experience');
  const { clearAppointmentInfo, setPersonId, setPersonName } = useSendFormsStore([
    'setPersonId',
    'setPersonName',
    'clearAppointmentInfo',
  ]);
  const { goToNextStep } = useSteps();

  function selectPersonHandler(_person: Person) {
    setPersonId(_person?.personId);
    setPersonName({
      firstName: _person?.firstName ?? '',
      lastName: _person?.lastName ?? '',
    });
    clearAppointmentInfo();
    goToNextStep();
  }

  if (isLoading) {
    return <ContentLoader show message={t('Loading contacts...')} />;
  }

  if (!persons.length) {
    return (
      <>
        <div css={imageContainerStyle}>
          <EmptyStateGraphicGeneric css={imageStyle} />
        </div>
        <Text css={centerTextStyle}>{t('No data to display')}</Text>
      </>
    );
  }

  return <PersonSelectorComponents.PersonSelectionList persons={persons} onSelect={selectPersonHandler} />;
};

const RecipientSelectionStep: FC = () => {
  const { locationId } = useSendFormsStore(['locationId']);
  const locationIds = useMemo(() => [locationId], [locationId]);
  const { persons, isLoading, filtersProps } = PersonSelectorHooks.useSelectRecipientList({
    groupIds: locationIds,
    shouldFilterWithoutContactInfo: false,
  });

  return (
    <div css={containerStyle}>
      <div css={filterSpacingStyle}>
        <PersonSelectorComponents.PersonSelectionListFilters {...filtersProps} />
      </div>
      <div css={listContainerStyle}>
        <Persons persons={persons} isLoading={isLoading} />
      </div>
    </div>
  );
};

export default RecipientSelectionStep;
